/**
 * 02 · Menü. Handover §5.3, §5.6 and §5.8, with `build/FIXES.md` items 1
 * (the second row of chips), 2 (the *Mennyiség* card retired and its two
 * links moved to the foot of this section), 3, 5, 6 and 7.
 *
 * The menu reads as a recommendation, not a catalogue (§3 of
 * `docs/overnight_constraints.md`): the dishes Planteen recommends are
 * already ticked, each section shows what is ticked in it and one quiet
 * control reveals the rest, and nothing on the page shows a price or a
 * quantity per dish.
 */

import { useState } from 'react'

import { toNumber } from '@plt/engine'

import type { PageBundle } from '../bundle.ts'
import { ALLERGEN_NAMES, CATEGORY_LABELS } from '../catalogue.ts'
import { AllergenText, Choices, DishName, LookalikeFootnote, Note, PickRow, PlantBasedTag } from '../components.tsx'
import type { MenuDish } from '../model.ts'
import { descriptionMarkedIn, lookalikeIn, nameMarksIn, rankedDishes } from '../model.ts'
import { MenuCheckLines } from './Notices.tsx'
import type { Filter } from '../state.ts'
/* `build/FIXES.md` item 79 (F8): one component for the confirmation note, so
   the banner above the menu, the note F1 shows and the note above the order
   button are the same four sentences. */
import { ConfirmationNote, unitWord } from './Step4Summary.tsx'

export function Step2Menu({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, set, available, occasion, limits, selection, priced, rules } = page
  /* DECIDED 20261101: the first row of dish cards (three) loads its photos at
     once; everything below it lazily. Counted down as the groups render. */
  let eagerLeft = 3
  const glutenFreeAuto = priced.glutenFreeAuto
  const guests = priced.guests
  const glutenFree = priced.glutenFreeGuests

  /* §5.3 pre-selection order, across both sides, so a section lists its
     dishes in the order they were ranked rather than in catalogue order. */
  const order = new Map(
    [...rankedDishes(available, occasion, 'sav'), ...rankedDishes(available, occasion, 'sw')].map(
      (dish, index) => [dish.id, index],
    ),
  )

  const isOn = (dish: MenuDish): boolean =>
    (dish.side === null ? false : selection[dish.side].includes(dish.id)) ||
    glutenFreeAuto.has(dish.id)

  const sideFull = (side: 'sav' | 'sw'): boolean => selection[side].length >= limits[side].limit

  /* The counts beside the chips and in the opening sentence are the dishes
     that are ticked, on the same basis as the rows: the gluten-free ones
     Planteen added are ticked and are counted (the defect vB6's own comment
     records — three figures for one thing on one screen). */
  const ticked = [...new Set([...selection.sav, ...selection.sw, ...glutenFreeAuto])]
  const countIn = (predicate: (dish: MenuDish) => boolean): number =>
    ticked.filter((id) => {
      const dish = priced.byId.get(id)
      return dish !== undefined && predicate(dish)
    }).length

  const onMenu = (side: 'sav' | 'sw'): number =>
    new Set([
      ...selection[side],
      ...[...glutenFreeAuto].filter((id) => priced.byId.get(id)?.side === side),
    ]).size
  const savouryCount = onMenu('sav')
  const sweetCount = onMenu('sw')

  const drinksOn = Object.values(state.drinks).filter((drink) => drink.on).length

  /* §5.3, one row: Mind · Sós · Édes · Gluténmentes · Italok. Gluténmentes is
     a need, not a category. The Gyümölcs chip is gone (Barna, 3 October); the
     fruit block itself (§5.6) stays and is reached under Mind. */
  const chips: { value: Filter; label: string; pill: number }[] = [
    { value: 'all', label: t('Mind', 'All'), pill: 0 },
    { value: 'sav', label: t('Sós', 'Savoury'), pill: countIn((dish) => dish.side === 'sav') },
    { value: 'sw', label: t('Édes', 'Sweet'), pill: countIn((dish) => dish.side === 'sw') },
    {
      value: 'gm',
      label: t('Gluténmentes', 'Gluten-free'),
      pill: countIn((dish) => dish.glutenFreeEligible),
    },
    { value: 'ital', label: t('Italok', 'Drinks'), pill: drinksOn },
  ]

  /* The second row, restored by build/FIXES.md item 1, exactly as §5.3
     specifies: under Sós and Édes only, each chip carrying how many dishes
     are ticked in it, hidden when fewer than two of its subgroups have a dish
     published that day, and reset to Mind when the first-row chip changes. */
  /* Which page groups sit under this side, and in which order: both out of
     the register, through `menu_groups`. The port carried a hand-written copy
     of the list for a while and the spec audit was right that it was a second
     copy of register data with nothing keeping the two together. */
  const subSide = state.filter === 'sav' ? 'sav' : state.filter === 'sw' ? 'sw' : null
  const subSideCategory = subSide === 'sav' ? 'sos' : subSide === 'sw' ? 'edes' : null
  const subKeys =
    subSideCategory === null
      ? []
      : page.groups
          .filter((group) => group.side === subSideCategory)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((group) => group.key)
          .filter((key) => available.some((dish) => dish.side === subSide && dish.group === key))
  const showSubRow = subKeys.length >= 2
  const activeSub = showSubRow && state.sub !== null && subKeys.includes(state.sub) ? state.sub : null

  const inSub = (dish: MenuDish): boolean => activeSub === null || dish.group === activeSub
  const shown = (dish: MenuDish): boolean => {
    if (state.filter === 'all') return true
    if (state.filter === 'sav') return dish.side === 'sav' && inSub(dish)
    if (state.filter === 'sw') return dish.side === 'sw' && inSub(dish)
    if (state.filter === 'gm') return dish.glutenFreeEligible
    return false
  }

  /* DR-4 (build/FIXES.md item 7), option B. A subgroup chip or Gluténmentes
     shows the whole section, because there the customer has narrowed the list
     themselves. */
  const collapsing = ['all', 'sav', 'sw'].includes(state.filter) && activeSub === null

  /* Run 15 item 1(e): the one sentence that belongs under the menu once the
     headcount, the appetite or the balance has moved and a dish the customer
     changed has held its quantity through it. */
  const heldQuantities = state.quantitiesHeld && Object.keys(page.quantities).length > 0

  /* Run 10 item 7b: which names this section draws right now — the dish rows
     each group shows, the fruit and drink rows, and the gluten-free names in
     the intro and the card — so the footnote shows exactly when one of them
     is marked. */
  const collapsedOpen = (key: string): boolean => !collapsing || state.sections.includes(key)
  const rows: MenuDish[] = [
    ...(state.filter === 'ital'
      ? []
      : available.filter(
          (dish) =>
            (dish.side === 'sav' || dish.side === 'sw') &&
            shown(dish) &&
            (collapsedOpen(dish.group) || isOn(dish)),
        )),
    ...(state.filter === 'all' ? page.fruitAvailable : []),
    ...(state.filter === 'all' || state.filter === 'ital' ? page.drinks : []),
  ]
  const drawn: MenuDish[] = [
    ...rows,
    /* Run 17 item B5(b): the gluten-free card and the intro name every dish
       that carries a share, not only the ones Planteen added, so the
       look-alike footnote follows the same list. */
    ...(glutenFree > 0
      ? [...priced.glutenFreeShares.keys()]
          .map((id) => priced.byId.get(id))
          .filter((dish): dish is MenuDish => dish !== undefined)
      : []),
  ]
  // Run 12 item 4d: a marked term in a card's description carries an
  // asterisk too, so it shows the footnote as a marked name does. Only the
  // card rows print a description; the gluten-free names do not.
  const lookalikeOnScreen =
    drawn.some((dish) => lookalikeIn(dish, page.t.lang)) ||
    rows.some((dish) => descriptionMarkedIn(dish, page.t.lang))

  return (
    <section id="sec2">
      <div className="sechead">
        <span className="n">02</span>
        <h2>{t('Menü', 'Menu')}</h2>
        {/* Run 10 item 7a, the second of the three places. */}
        <PlantBasedTag page={page} id="pbMenu" />
      </div>

      <div id="menuTop">
        {/* `build/FIXES.md` item 79 (F8): the four §5.9 reasons that used to
            put *Ajánlatkérés lesz belőle.* here are confirmation reasons now.
            The banner keeps saying **why** — the customer answered every one
            of them and is entitled to see which answer did it — and the
            confirmation note says what happens next, in the brief's own
            words. The enquiry banner below it is unchanged and is reached by
            §8.3's short-notice route alone, which item 79 does not touch. */}
        {/* Barna, 10 October 2026: the reasons are for staff only. The
            customer sees the confirmation note and nothing about why — the
            list printed empty items for the silent reasons and named the
            value threshold. Staff read the reasons in the console and C2. */}
        {rules.enquiry.length === 0 && rules.confirmation.length > 0 ? (
          <ConfirmationNote page={page} />
        ) : null}
        {rules.enquiry.length > 0 ? (
          <Note tone="stop">
            <b>{t('Ajánlatkérés lesz belőle.', 'This becomes an enquiry.')}</b>{' '}
            {rules.enquiry
              .map((reason, index) => (index === 0 ? reason[0]?.toUpperCase() + reason.slice(1) : reason))
              .join('; ')}
            .{' '}
            {t(
              'A menüt nyugodtan összeállíthatja: mindent megkapunk, amit megad, és kollégánk ez alapján hívja fel.',
              'Feel free to build the menu: we receive everything you enter, and a colleague calls you on that basis.',
            )}
          </Note>
        ) : null}
      </div>

      <div className="card">
        <p id="menuIntro">
          <b className="rec">
            {/* Copy fixes of 11 October 2026, item 1 (supersedes run 10 item 1):
                the first sentence only; the count sentence after it is generated
                and unchanged. */}
            {t('Erre az alkalomra ezt a menüt ajánljuk.', 'We recommend this menu for the occasion.')}
          </b>
          {t.lang === 'hu' ? (
            <>
              {' '}
              {f.n(savouryCount + sweetCount)} féle ételt válogattunk: {f.n(savouryCount)} sósat és{' '}
              {f.n(sweetCount)} édeset. Cserélhet, hozzáadhat vagy elvehet ételeket.
            </>
          ) : (
            <>
              {' '}
              We have chosen {f.n(savouryCount + sweetCount)} different dishes: {f.n(savouryCount)}{' '}
              savoury and {f.n(sweetCount)} sweet. Swap, add or remove dishes.
            </>
          )}
          {/* Run 15 item 1(i): the one explanatory sentence the menu
              introduction carries, in place of the *Többet kérek ebből*
              line §5.3 used to put here. */}
          <span className="sub" style={{ display: 'block', marginTop: 6 }} data-testid="qty-intro">
            {t(
              'A − és + gombbal bármelyik étel mennyiségét módosíthatja.',
              'Use − and + to change the quantity of any dish.',
            )}
          </span>
          {glutenFree > 0 && priced.glutenFreeShares.size > 0 ? (
            <span className="sub" style={{ display: 'block', marginTop: 6 }}>
              <b>
                {t(
                  `${f.n(glutenFree)} gluténmentes vendégnek:`,
                  `For ${f.n(glutenFree)} gluten-free guest${f.plural(glutenFree, '', 's')}:`,
                )}
              </b>{' '}
              {joinNodes(
                [...priced.glutenFreeShares.keys()]
                  .map((id) => priced.byId.get(id))
                  .filter((dish): dish is MenuDish => dish !== undefined)
                  .map((dish) => <NameOf key={dish.id} page={page} dish={dish} />),
                ', ',
              )}
              .{' '}
              {t('Pipával jelöltük, külön dobozban küldjük.', 'Ticked below, sent in separate boxes.')}
            </span>
          ) : null}
        </p>

        <Choices
          id="filters"
          dataAttribute="filt"
          choices={chips.map((chip) => ({ value: chip.value, label: chip.label, pill: chip.pill }))}
          value={state.filter}
          onChoose={(value) => set({ filter: value, sub: null })}
        />

        {showSubRow ? (
          <Choices
            id="subfilters"
            className="opts subf"
            dataAttribute="sub"
            choices={[
              { value: '', label: t('Mind', 'All') },
              ...subKeys.map((key) => {
                const group = page.groups.find((candidate) => candidate.key === key)
                return {
                  value: key,
                  label: t(group?.nameHu ?? key, group?.nameEn ?? key),
                  pill: countIn((dish) => dish.side === subSide && dish.group === key),
                }
              }),
            ]}
            value={activeSub ?? ''}
            onChoose={(value) => set({ sub: value === '' ? null : value })}
          />
        ) : (
          <div className="opts subf hide" id="subfilters" />
        )}

        <div id="limitNote">
          {/* DR-21: shown while the limits that removed the dishes are the ones
              in force, and while a side is still at its limit. */}
          {page.clipped && (sideFull('sav') || sideFull('sw')) ? (
            <Note>
              {t(
                'Ennyi vendégnél kevesebb fogás választható, ezért a legkevésbé ajánlott fogásokat kivettük.',
                'Fewer dishes can be chosen for this many guests, so we have removed the ones we recommend least.',
              )}
            </Note>
          ) : null}
          {(['sav', 'sw'] as const)
            .filter((side) => sideFull(side) && state.touched[side])
            .map((side) => (
              <Note key={side}>
                {side === 'sw'
                  ? t(
                      `Elérte a legtöbb választható édes fogást (${limits.sw.limit} féle). Másikat úgy választhat, ha egyet elvesz.`,
                      `You can choose up to ${limits.sw.limit} different sweet dishes. To pick another, remove one first.`,
                    )
                  : t(
                      `Elérte a legtöbb választható sós fogást (${limits.sav.limit} féle). Másikat úgy választhat, ha egyet elvesz.`,
                      `You can choose up to ${limits.sav.limit} different savoury dishes. To pick another, remove one first.`,
                    )}
              </Note>
            ))}
        </div>

        {/* Run 17 item B4 (DECIDED 20261015, Barna): the live check against
            the recommendation, directly above the dish list, one line per
            side. Both are redrawn from `state` on every render, so they move
            with - / +, the headcount, the appetite, the balance, *Vissza az
            ajanlotthoz* and *Visszavonas* without any of those having to know
            about them. */}
        <MenuCheckLines page={page} id="menuCheck" />

        <div id="dishList">
          {state.filter === 'gm' ? (
            <Note tone="ok">
              {glutenFree > 0 && priced.glutenFreeShares.size > 0
                ? t(
                    `${f.article(glutenFree, true)} ${f.n(glutenFree)} gluténmentes vendég ezeket kapja, külön dobozban és külön címkével. Automatikusan beválogattuk őket.`,
                    `The ${f.n(glutenFree)} gluten-free guest${f.plural(glutenFree, '', 's')} get${f.plural(glutenFree, 's', '')} these, in separate, labelled boxes. We included them automatically.`,
                  )
                : t(
                    'Ha vendégei közül valakinek gluténmentesen kell étkeznie, adja meg a létszámukat az 1. lépésben: nekik külön dobozban, külön címkével készítünk.',
                    'If any of your guests must eat gluten-free, enter how many in step 1: we prepare theirs in separate, labelled boxes.',
                  )}
            </Note>
          ) : null}

          {state.filter === 'ital' ? null : (
            <div className={page.photos ? 'list' : 'list compact'}>
              {page.groups
                .filter((group) => group.side === 'sos' || group.side === 'edes')
                .map((group) => {
                  const inGroup = available
                    .filter((dish) => dish.group === group.key && shown(dish))
                    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0))
                  if (inGroup.length === 0) return null
                  const opened = !collapsing || state.sections.includes(group.key)
                  const rest = inGroup.filter((dish) => !isOn(dish)).length
                  const visible = opened ? inGroup : inGroup.filter(isOn)
                  const firstRow = eagerLeft
                  eagerLeft = Math.max(0, eagerLeft - visible.length)
                  return (
                    <div key={group.key}>
                      <h3 className="cathd">{t(group.nameHu, group.nameEn)}</h3>
                      {visible.map((dish, index) => (
                        <DishRow
                          key={dish.id}
                          page={page}
                          dish={dish}
                          selected={isOn(dish)}
                          sideFull={sideFull}
                          eager={index < firstRow}
                        />
                      ))}
                      {collapsing && rest > 0 ? (
                        <button
                          type="button"
                          className="secmore"
                          data-open={group.key}
                          aria-expanded={opened}
                          onClick={() =>
                            set((current) => ({
                              sections: current.sections.includes(group.key)
                                ? current.sections.filter((key) => key !== group.key)
                                : [...current.sections, group.key],
                            }))
                          }
                        >
                          {opened
                            ? t('Elrejtés', 'Hide')
                            : t(
                                `További ${f.n(rest)} fogás`,
                                `${f.n(rest)} more dish${rest === 1 ? '' : 'es'}`,
                              )}
                        </button>
                      ) : null}
                    </div>
                  )
                })}
            </div>
          )}
        </div>

        {/* Run 15 item 1(e) and 1(f): said once, directly under the dish
            list — the place DR-3 (build/FIXES.md item 6, option C) put the
            sentence the emphasis used to need, for the same reason. */}
        <div id="qtyNote">
          {heldQuantities ? (
            <p className="sub" style={{ marginTop: 6 }} data-testid="qty-held">
              {t(
                'Az Ön által módosított ételek mennyisége nem változott.',
                'Dishes you changed kept their quantities.',
              )}
            </p>
          ) : null}
          <RemovalUndo page={page} />
        </div>

        <Fruit page={page} />
        <Drinks page={page} />

        <div id="gfCard">
          {/* Run 9, item 5 (DECIDED 20261010): one labelled box per side for
              the gluten-free guests. The card shows, per side, the dish name
              and the gluten-free quantity; a side with no gluten-free dish is
              named, and nothing blocks or reroutes the order. */}
          {glutenFree > 0 ? (
            priced.glutenFreeShares.size === 0 ? (
              <Note>
                {t(
                  'Erre a dátumra nincs gluténmentes fogásunk.',
                  'We have no gluten-free dish for this date.',
                )}
              </Note>
            ) : (
              <Note tone="ok">
                <b>
                  {t(
                    `${f.n(glutenFree)} gluténmentes vendégnek`,
                    `For ${f.n(glutenFree)} gluten-free guest${f.plural(glutenFree, '', 's')}`,
                  )}
                </b>{' '}
                {t(
                  `(${f.article(guests)} ${f.n(guests)} fő között) oldalanként külön, címkézett dobozt állítunk össze:`,
                  `(within the ${f.n(guests)} ${f.plural(guests, 'guest', 'guests')}) we pack a separate, labelled box for each side:`,
                )}
                {/* Run 17 item B5(b): every dish that carries something for
                    the gluten-free guests, whether Planteen added it for them
                    or it was already on the menu and their share went on to
                    its own quantity. The figure is the share, never the
                    batch. */}
                {(['sav', 'sw'] as const).map((side) => {
                  const lines = priced.priced.sides[side].all.filter(
                    (line) => (line.glutenFreePortions ?? 0) > 0,
                  )
                  if (lines.length === 0) return null
                  return (
                    <span key={side} className="gfside" data-gfside={side} style={{ display: 'block' }}>
                      {side === 'sav' ? t('Sós: ', 'Savoury: ') : t('Édes: ', 'Sweet: ')}
                      {joinNodes(
                        lines.map((line) => {
                          const share = line.glutenFreePortions ?? 0
                          return (
                            <span key={line.dish.id}>
                              <NameOf page={page} dish={priced.byId.get(line.dish.id)} />
                              {` (${f.n(share)} ${unitWord(page, line.dish.unit, share)})`}
                            </span>
                          )
                        }),
                        ', ',
                      )}
                    </span>
                  )
                })}
                {priced.glutenFreeMissing.sav ? (
                  <span style={{ display: 'block' }}>
                    {t(
                      'Erre a dátumra nincs gluténmentes sós fogásunk.',
                      'We have no gluten-free savoury dish for this date.',
                    )}
                  </span>
                ) : null}
                {priced.glutenFreeMissing.sw ? (
                  <span style={{ display: 'block' }}>
                    {t(
                      'Erre a dátumra nincs gluténmentes édes fogásunk.',
                      'We have no gluten-free sweet dish for this date.',
                    )}
                  </span>
                ) : null}
              </Note>
            )
          ) : null}
        </div>

        {/* Run 10 item 7b: the footnote, once, when a marked name is on screen. */}
        <LookalikeFootnote page={page} id="menuLookalikeNote" show={lookalikeOnScreen} />

        <div id="menuWarn">
          {/* DR-2 (build/FIXES.md item 5): the minimum batch, the dish names and
              the reason are gone from the customer page; what is left is what to
              do about it. */}
          {priced.belowMinimumProduction ? (
            <Note tone="stop">
              {t(
                'Ennyi vendéghez ez túl sokféle fogás. Kérjük, válasszon kevesebbet.',
                'That is too many different dishes for this many guests. Please choose fewer.',
              )}
            </Note>
          ) : null}
        </div>

        {/* The end of section 02, under the dish list and the drinks
            (build/FIXES.md item 2). The Mennyiség card that used to hold these
            two is retired with the quantity level. */}
        <details style={{ marginTop: 12 }}>
          <summary className="sub" id="alSum" style={{ cursor: 'pointer', fontWeight: 600 }}>
            {t('Mit jelentenek az allergénszámok?', 'What do the allergen numbers mean?')}
          </summary>
          <div className="sub" id="alBody" style={{ marginTop: 8 }}>
            {Object.entries(ALLERGEN_NAMES).map(([number, label], index) => (
              <span key={number}>
                {index > 0 ? ' · ' : ''}
                <b>{number}</b> {t.of(label)}
              </span>
            ))}
          </div>
        </details>

        <p className="sub" id="customLink" style={{ marginTop: 10 }}>
          {state.custom ? (
            <>
              {t('Egyedi menüt kért.', 'You asked for a custom menu.')}{' '}
              <button
                type="button"
                className="linkish"
                id="custOff"
                onClick={() => set({ custom: false })}
              >
                {t('Mégis a fenti menüből rendelek', 'Order from the menu above after all')}
              </button>
            </>
          ) : (
            <>
              {t('Egyedi menüt szeretne?', 'Would you like a custom menu?')}{' '}
              <button
                type="button"
                className="linkish"
                id="custOn"
                onClick={() => set({ custom: true })}
              >
                {t('Ajánlatot kérek', 'Request an offer')}
              </button>
            </>
          )}
        </p>
      </div>
    </section>
  )
}

/** A dish name in running text, marked when it is a plant-based look-alike
 *  (run 10 item 7b). */
function NameOf(props: { page: PageBundle; dish: MenuDish | undefined }): JSX.Element | null {
  const { page, dish } = props
  if (dish === undefined) return null
  return <DishName label={page.t(dish.nameHu, dish.nameEn)} marks={nameMarksIn(dish, page.t.lang)} />
}

/** Nodes with a separator between them, as `join` does for strings. */
function joinNodes(nodes: JSX.Element[], separator: string): JSX.Element[] {
  return nodes.flatMap((node, index) => (index === 0 ? [node] : [<span key={`sep${index}`}>{separator}</span>, node]))
}

/**
 * One dish row. The four states of §6 of the constraints are the row's
 * attributes: ticked, available, emphasised, unavailable.
 *
 * DR-17 (`build/FIXES.md` item 20), option B: every allergen line reads
 * *allergének (példa)* while the allergen block is a test marking. The back
 * end turns that off only on a dish whose verification is real — the flag
 * comes out of the publication, so a dish row read on its own, or
 * screenshotted, never makes a flat allergen claim it cannot stand behind.
 */
function DishRow(props: {
  page: PageBundle
  dish: MenuDish
  selected: boolean
  sideFull: (side: 'sav' | 'sw') => boolean
  eager?: boolean
}): JSX.Element {
  const { page, dish, selected, sideFull } = props
  const { t, state, set, selection, priced, locked } = page
  const side = dish.side
  const auto = side !== null && priced.glutenFreeAuto.has(dish.id) && !selection[side].includes(dish.id)
  /* Run 15 item 1(d): what is made of this dish, and whether the customer set it. */
  const quantity = priced.quantities.get(dish.id) ?? null
  const changed = page.quantities[dish.id] !== undefined
  const chosen = side === null ? false : selection[side].includes(dish.id)
  const piecesPerGuest =
    side === 'sw' ? toNumber(priced.priced.recommended.sweet) : toNumber(priced.priced.recommended.savoury)
  /* The greyed-out treatment is for a dish that cannot be added. The same flag
     sits on the last selected dish of a side, which cannot be removed — that
     one keeps its green ground, or "recommended" and "unavailable" would look
     the same. */
  const disabled =
    locked ||
    (side === null
      ? false
      : chosen
        ? selection[side].length <= 1 && piecesPerGuest > 0
        : sideFull(side))

  return (
    <PickRow
      dish={dish}
      eager={props.eager}
      kind={auto ? 'gfauto' : 'pick'}
      label={t(dish.nameHu, dish.nameEn)}
      lang={t.lang}
      selected={selected}
      disabled={disabled}
      changed={changed}
      title={
        auto
          ? t(
              'A gluténmentes vendégeknek automatikusan beválogatva',
              'Included automatically for the gluten-free guests',
            )
          : undefined
      }
      onZoom={page.openZoom}
      meta={<DishMeta page={page} dish={dish} auto={auto} />}
      onToggle={() => {
        if (side === null) return
        set((current) => {
          const selected_ = current.selection ?? { sav: [], sw: [] }
          const ids = selected_[side]
          const next = ids.includes(dish.id)
            ? ids.filter((id) => id !== dish.id)
            : [...ids, dish.id]
          /* Run 15 item 1(f): a dish taken off the menu by its own card
             leaves its quantity behind, and a dish re-added this way comes
             back on its recommendation. The undo note belongs to the stepper
             alone, so any other route clears it. */
          const { [dish.id]: _dropped, ...rest } = current.quantities
          return {
            selection: { ...selected_, [side]: next },
            touched: { ...current.touched, [side]: true },
            clipped: false,
            quantities: ids.includes(dish.id) ? rest : current.quantities,
            removed: null,
          }
        })
      }}
    >
      {chosen && !state.sent && side !== null && quantity !== null ? (
        <QuantityStepper
          page={page}
          dish={dish}
          side={side}
          ordered={quantity.ordered}
          unit={quantity.unit}
          changed={changed}
          disabled={locked}
        />
      ) : null}
    </PickRow>
  )
}

/**
 * Run 15 item 1(b), (c), (d) and (f), which replaces *Többet kérek ebből*.
 *
 * − and + move this dish and no other, by its own production unit (§6.7 step
 * 5), and the price follows at once because the whole page is priced from
 * `state` on every render.
 *
 * **Run 16 item 1 (DECIDED 20261015, Barna) puts the figure between them**:
 * *{n} db* / *{n} pcs*, the dish's own line quantity. This replaces §4's
 * Version B principle that quantities per dish do not appear in the menu
 * builder — the ruling is recorded in §4 and §5.5 of `docs/handover_rev7.md`.
 *
 * Every control and label is keyed by **dish ID**, which is run 14's
 * guarantee carried over to the control that replaces the emphasis: each
 * handler reads the row's own `dish`, and nothing is looked up by a position
 * in a list.
 */
function QuantityStepper(props: {
  page: PageBundle
  dish: MenuDish
  side: 'sav' | 'sw'
  ordered: number
  unit: number
  changed: boolean
  disabled: boolean
}): JSX.Element {
  const { page, dish, side, ordered, unit, changed, disabled } = props
  const { t, f, set } = page
  const label = t(dish.nameHu, dish.nameEn)

  const step = (by: number): void => {
    set((current) => {
      const next = ordered + by * unit
      /* Item 1(f): − at one production unit takes the dish off the menu,
         exactly as the card's own remove does, and the note below offers it
         back at the quantity it had. */
      if (next < unit) {
        const selected = current.selection ?? { sav: [], sw: [] }
        const { [dish.id]: _dropped, ...rest } = current.quantities
        return {
          selection: { ...selected, [side]: selected[side].filter((id) => id !== dish.id) },
          touched: { ...current.touched, [side]: true },
          clipped: false,
          quantities: rest,
          removed: { dishId: dish.id, side, portions: ordered },
        }
      }
      return { quantities: { ...current.quantities, [dish.id]: next }, removed: null }
    })
  }

  const restore = (): void =>
    set((current) => {
      const { [dish.id]: _dropped, ...rest } = current.quantities
      return { quantities: rest, removed: null }
    })

  return (
    <>
      {/* Item 1(c): the unit, where a dish is not made one at a time. */}
      {unit > 1 ? (
        <span className="sub qtyunit" data-qtyunit={dish.id}>
          {t(`${f.n(unit)} db / egység`, `${f.n(unit)} pcs per unit`)}
        </span>
      ) : null}
      <span className="stepper qtystep" data-qtystep={dish.id}>
        <button
          type="button"
          data-qtyminus={dish.id}
          aria-label={t(`${label}: kevesebb`, `${label}: fewer`)}
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation()
            step(-1)
          }}
        >
          −
        </button>
        {/* Run 16 item 1(a): the dish's own quantity, between − and +. It is
            `ordered`, the dish's line quantity, so it moves with every press
            and with every headcount, appetite or balance change, and it comes
            back on its recommendation when the dish does. */}
        <output className="num qtynow" data-qtynow={dish.id}>
          {t(`${f.n(ordered)} db`, `${f.n(ordered)} pcs`)}
        </output>
        <button
          type="button"
          data-qtyplus={dish.id}
          aria-label={t(`${label}: több`, `${label}: more`)}
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation()
            step(1)
          }}
        >
          +
        </button>
      </span>
      {/* Item 1(d): the mark and the way back to the recommendation. */}
      {changed ? (
        <span className="sub qtychanged" data-qtychanged={dish.id}>
          {t('Ön módosította', 'Changed by you')}{' '}
          <button
            type="button"
            className="linkish"
            data-qtyreset={dish.id}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation()
              restore()
            }}
          >
            {t('Vissza az ajánlotthoz', 'Back to recommended')}
          </button>
        </span>
      ) : null}
    </>
  )
}

/**
 * Run 15 item 1(f): − at one production unit takes the dish off the menu and
 * says so, with one way back that puts it there at the quantity it had. Any
 * other route to the same dish brings it back on its recommendation, which is
 * why the quantity travels in `state.removed` rather than in `quantities`.
 */
function RemovalUndo({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, state, set, priced, locked } = page
  const removed = state.removed
  if (removed === null) return null
  const dish = priced.byId.get(removed.dishId)
  if (dish === undefined) return null
  const label = t(dish.nameHu, dish.nameEn)
  return (
    <p className="sub" data-testid="qty-removed" data-qtyremoved={removed.dishId}>
      {t(`${label} kikerült a menüből.`, `${label} was removed from the menu.`)}{' '}
      <button
        type="button"
        className="linkish"
        data-qtyundo={removed.dishId}
        disabled={locked}
        onClick={() =>
          set((current) => {
            const selected = current.selection ?? { sav: [], sw: [] }
            const back = selected[removed.side].includes(removed.dishId)
              ? selected[removed.side]
              : [...selected[removed.side], removed.dishId]
            return {
              selection: { ...selected, [removed.side]: back },
              touched: { ...current.touched, [removed.side]: true },
              clipped: false,
              quantities: { ...current.quantities, [removed.dishId]: removed.portions },
              removed: null,
            }
          })
        }
      >
        {t('Visszavonás', 'Undo')}
      </button>
    </p>
  )
}

function DishMeta(props: { page: PageBundle; dish: MenuDish; auto: boolean }): JSX.Element {
  const { page, dish, auto } = props
  const { t } = page
  const categoryLabel = CATEGORY_LABELS[dish.category]
  return (
    <>
      {categoryLabel === undefined ? dish.category : t.of(categoryLabel)}
      {' '}
      · <AllergenText page={page} dish={dish} />
      {auto ? (
        <>
          {' '}
          <span className="tag gmauto">{t('gluténmentes vendégeknek', 'for gluten-free guests')}</span>
        </>
      ) : null}
      {dish.featured ? (
        <>
          {' '}
          <span className="tag fav">{t('Kedvenc', 'Favourite')}</span>
        </>
      ) : null}
      {dish.glutenFreeEligible ? (
        <>
          {' '}
          <span className="tag gm">{t('gluténmentes', 'gluten-free')}</span>
        </>
      ) : null}
    </>
  )
}

/**
 * §5.6, the fruit block, between the dishes and the drinks. Its quantity is
 * Planteen's, never the customer's.
 *
 * The register has a *Gyümölcs* page group and the interim catalogue has no
 * row that publishes into it (§7.3). The block says so rather than drawing an
 * empty list or inventing a fruit item, which is what *mark the gap and mock
 * it, visibly* means here.
 */
function Fruit({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, f, state, set, fruitAvailable, num, groups, locked } = page
  if (state.filter !== 'all') return null
  const hasFruitGroup = groups.some((group) => group.side === 'gyumolcs')
  if (!hasFruitGroup) return null
  const limit = num('max_fruit_varieties')
  const full = state.fruit.length >= limit
  return (
    <div id="fruitWrap">
      <h3 className="cathd" id="fruitHd">
        {t('Gyümölcs', 'Fruit')}
      </h3>
      {fruitAvailable.length === 0 ? (
        <Note>
          {t(
            'A gyümölcskínálatot még összeállítjuk, ezért most nem választható. Ha gyümölcsöt szeretne, írja meg az étrendi igények mezőben, és egyeztetünk.',
            'We are still putting the fruit selection together, so it cannot be chosen yet. If you would like fruit, say so in the dietary needs field and we will talk it through.',
          )}
        </Note>
      ) : (
        <>
          <p className="sub" id="fruitIntro" style={{ marginTop: 0 }}>
            {state.occasion === 'coffee'
              ? t('Kávészünethez ajánljuk. ', 'Recommended for a coffee break. ')
              : ''}
            {t(
              `A mennyiséget mi adjuk meg. Legfeljebb ${f.n(limit)} féle.`,
              `We set the quantity. Up to ${f.n(limit)} items.`,
            )}
          </p>
          <div id="fruitList">
            <div className={page.photos ? 'list' : 'list compact'}>
              {fruitAvailable.map((dish) => {
                const on = state.fruit.includes(dish.id)
                return (
                  <PickRow
                    key={dish.id}
                    dish={dish}
                    kind="fruit"
                    label={t(dish.nameHu, dish.nameEn)}
                    lang={t.lang}
                    selected={on}
                    disabled={locked || (!on && full)}
                    onZoom={page.openZoom}
                    meta={<DishMeta page={page} dish={dish} auto={false} />}
                    onToggle={() =>
                      set((current) => ({
                        fruit: current.fruit.includes(dish.id)
                          ? current.fruit.filter((id) => id !== dish.id)
                          : current.fruit.length < limit
                            ? [...current.fruit, dish.id]
                            : current.fruit,
                      }))
                    }
                  />
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * §5.6, the drinks. Added only if the customer ticks one.
 *
 * **Run 15 item 2 (DECIDED 20261015, Barna)** answers the question run 14
 * left open. A drink the customer adds is a **total quantity**: the stepper
 * counts bottles, its step is the drink's own production unit, and the
 * headcount, the appetite and the balance never touch it. One figure, in one
 * unit, on the page, the offer card, the summary, the emails, the PDFs, the
 * kitchen views and the console — so run 14's two-unit line, which said both
 * *db / fő* and *összesen N db*, is gone with the model it explained.
 *
 * The lines the build adds for itself are untouched (item 2(c)): F3's
 * conference refreshment drink stays per guest and stays hidden while
 * `unit_volume_l` is empty, and F7's coffee stays as it was decided.
 */
function Drinks({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, state, set, drinks, locked, refreshment } = page
  if (!(state.filter === 'all' || state.filter === 'ital')) return null
  if (drinks.length === 0) return null
  return (
    <div id="drinkWrap">
      <h3 className="cathd" id="drinkHd">
        {t('Italok', 'Drinks')}
      </h3>
      <p className="sub" id="drinkIntro" style={{ marginTop: 0 }}>
        {t('Csak akkor kerül a rendelésbe, ha bejelöli.', 'Added only if you tick it.')}
      </p>
      <div id="drinkList">
        {drinks.map((dish) => {
          /* §6.7 step 5: a drink is made in whole catalogue units too, so one
             press is one unit — one bottle wherever the unit is 1, which is
             every drink row in the catalogue today. */
          const unit = Math.max(Math.round(dish.raw.production_unit ?? 1), 1)
          /* Run 17 item B12(c): F3's conference refreshment line is the one
             drink the build counts out for itself, and item B6 says it is
             *a normal drinks line the customer can change*. So it is drawn
             ticked, at the figure F3 works out, until the customer answers
             for it — after which `state.drinks` is the answer and run 15's
             rule holds: it does not follow a later headcount change. */
          const computed = refreshment?.dish.id === dish.id ? refreshment : null
          const entry =
            state.drinks[dish.id] ??
            (computed === null
              ? { on: false, bottles: unit }
              : { on: true, bottles: computed.portions })
          return (
            <div className="xrow" key={dish.id}>
              <label className="chk">
                <input
                  type="checkbox"
                  data-x={dish.id}
                  checked={entry.on}
                  disabled={locked}
                  onChange={(event) =>
                    set((current) => ({
                      drinks: {
                        ...current.drinks,
                        [dish.id]: {
                          on: event.target.checked,
                          bottles: current.drinks[dish.id]?.bottles ?? unit,
                        },
                      },
                    }))
                  }
                />
                <span>
                  <DishName label={t(dish.nameHu, dish.nameEn)} marks={nameMarksIn(dish, t.lang)} />
                  {computed !== null && state.drinks[dish.id] === undefined ? (
                    <span className="sub" data-refreshment={dish.id}>
                      {' '}
                      {t(
                        'A konferencia alkalmaihoz ajánljuk.',
                        'Recommended for the conference occasions.',
                      )}
                    </span>
                  ) : null}
                </span>
              </label>
              {entry.on ? (
                <DrinkQuantity page={page} dish={dish} unit={unit} bottles={entry.bottles} />
              ) : (
                <span />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * **Run 17 item B6 (DECIDED 20261015, Barna): faster drink entry.**
 *
 * The quantity between − and + is a **field the customer can type into**, in
 * whole bottles, and **0 removes the drink** — the one place a drink can
 * leave the order other than the tick box. Beside it, *Vendégenként 1* / *1
 * per guest* sets the drink to the current headcount.
 *
 * The figure does **not** follow a later headcount change, which is run 15's
 * rule for a quantity the customer set: a drink the customer counted out is a
 * whole quantity and nothing multiplies it by anybody.
 *
 * What is typed is held as typed while the field has focus, so a half-typed
 * number is not corrected under the hand (the same rule as the headcount
 * field in step 01). Non-numeric input is refused: the field keeps the last
 * figure the page could read, and the page's own quantity never becomes NaN.
 */
function DrinkQuantity(props: {
  page: PageBundle
  dish: MenuDish
  unit: number
  bottles: number
}): JSX.Element {
  const { page, dish, unit, bottles } = props
  const { t, set, locked, priced } = page
  const label = t(dish.nameHu, dish.nameEn)
  const [typing, setTyping] = useState<string | null>(null)
  const shown = typing ?? String(bottles)

  /** What the page keeps: whole bottles, never below nothing, never above the
   *  ceiling. 0 is kept and means *take this drink off the order*. */
  const commit = (raw: string): void => {
    const text = raw.trim()
    if (text === '') return
    if (!/^\d+$/.test(text)) return
    const asked = Math.min(Math.max(Number(text), 0), DRINK_CEILING)
    set((current) => {
      const held = current.drinks[dish.id] ?? { on: true, bottles: unit }
      /* Item B6: *0 removes the drink*, which is the tick box's own answer,
         so the row goes back to the figure it opens on. */
      if (asked === 0) {
        return { drinks: { ...current.drinks, [dish.id]: { on: false, bottles: unit } } }
      }
      const whole = Math.max(Math.ceil(asked / unit) * unit, unit)
      return { drinks: { ...current.drinks, [dish.id]: { ...held, on: true, bottles: whole } } }
    })
  }

  return (
    <div className="stepper drinkqty">
      <button
        type="button"
        data-xm={dish.id}
        aria-label={t(`${label}: eggyel kevesebb`, `${label}: one fewer`)}
        disabled={locked}
        onClick={() => {
          setTyping(null)
          step(page, dish.id, -unit)
        }}
      >
        −
      </button>
      <input
        className="num drinkfield"
        type="text"
        inputMode="numeric"
        data-xn={dish.id}
        data-xfield={dish.id}
        aria-label={t(`${label}: mennyiség`, `${label}: quantity`)}
        value={shown}
        disabled={locked}
        onChange={(event) => {
          const raw = event.target.value
          /* Item B6: non-numeric input is refused — the field does not take
             it at all, so there is nothing to correct afterwards. */
          if (raw !== '' && !/^\d+$/.test(raw.trim())) return
          setTyping(raw)
          commit(raw)
        }}
        onBlur={() => setTyping(null)}
      />
      <button
        type="button"
        data-xp={dish.id}
        aria-label={t(`${label}: eggyel több`, `${label}: one more`)}
        disabled={locked}
        onClick={() => {
          setTyping(null)
          step(page, dish.id, unit)
        }}
      >
        +
      </button>
      <span className="sub">{unitWord(page, dish.engine?.unit ?? 'db', bottles)}</span>
      <button
        type="button"
        className="linkish drinkperguest"
        data-xperguest={dish.id}
        disabled={locked}
        onClick={() => {
          setTyping(null)
          commit(String(priced.guests))
        }}
      >
        {t('Vendégenként 1', '1 per guest')}
      </button>
    </div>
  )
}

/**
 * Run 15 item 2(a): the stepper counts bottles and its step is the drink's
 * production unit. It never goes below one unit — a ticked drink with none
 * of it is not an answer; the tickbox is how a drink leaves the order — and
 * `DRINK_CEILING` is where §5.6's old bound of four per guest ends up: four
 * per guest at the 400-guest ceiling of §5.2, so no order the page could
 * place before is out of reach now.
 */
export const DRINK_CEILING = 1600

function step(page: PageBundle, dishId: string, by: number): void {
  const unit = Math.max(Math.abs(by), 1)
  page.set((current) => {
    const entry = current.drinks[dishId] ?? { on: true, bottles: unit }
    return {
      drinks: {
        ...current.drinks,
        [dishId]: { ...entry, bottles: Math.max(unit, Math.min(DRINK_CEILING, entry.bottles + by)) },
      },
    }
  })
}
